import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useModulesStore } from '@/stores/modules'
import { Alert, Fade, TextField } from '@mui/material'
import axios from 'axios'
import { useEffect, useState } from 'react'
import { INewModule } from '../../../../@types'
import { CardModule, Modal } from '../../../../components'
import { useInputText } from '../../../../hooks'
import services from '../../../../services'
import { ModalRegisterProps } from '../@types'

export default function ModalUpdate({ open, onDone }: ModalRegisterProps) {
  const { selectedItem, clearSelectedItem } = useModulesStore()
  const initialValues = { level: 1, name: '', description: '' }
  const input = useInputText(initialValues)

  const [activeIcon, setActiveIcon] = useState<File | null>(null)
  const [disabledIcon, setDisabledIcon] = useState<File | null>(null)

  useEffect(() => {
    if (selectedItem) {
      selectedItem.number && input.updateInitial('number', selectedItem.number)
      selectedItem.level && input.updateInitial('level', selectedItem.level)
      selectedItem.name && input.updateInitial('name', selectedItem.name)
      selectedItem.description &&
        input.updateInitial('description', selectedItem.description)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem])

  useEffect(() => {
    setActiveIcon(null)
    setDisabledIcon(null)
  }, [])

  const onActiveIconChange = (file: typeof activeIcon) => setActiveIcon(file)

  const onDisabledIconChange = (file: typeof disabledIcon) =>
    setDisabledIcon(file)

  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  const closeModal = () => {
    clearSelectedItem()
    setError(null)
    input.clear()
    onDone()
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    try {
      if (error) setError(null)
      setLoading(true)

      const { name, level, description } = input
      const data: INewModule = {} as INewModule

      if (name) data.name = name
      if (description) data.description = description
      if (level) data.level = parseInt(String(level))

      if (activeIcon) {
        data.activeIcon = {
          filename: activeIcon.name,
          contentType: activeIcon.type,
        }
      }

      if (disabledIcon) {
        data.disableIcon = {
          filename: disabledIcon.name,
          contentType: disabledIcon.type,
        }
      }

      const updatedModule = await services.modules.updateById(
        selectedItem!.id,
        data,
      )

      if ((disabledIcon || activeIcon) && updatedModule?.uploadUrls) {
        if (activeIcon) {
          try {
            await axios.put(updatedModule!.uploadUrls![0], activeIcon!, {
              headers: { 'Content-Type': activeIcon!.type },
            })
          } catch (error) {
            throw new Error('Falha ao carregar o Active Icon.')
          }
        }

        if (disabledIcon) {
          try {
            await axios.put(updatedModule!.uploadUrls![1], disabledIcon!, {
              headers: { 'Content-Type': disabledIcon!.type },
            })
          } catch (error) {
            throw new Error('Falha ao carregar o Disable Icon.')
          }
        }
      }

      closeModal()
    } catch (error) {
      setError((error as Error).message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={closeModal}
      head={<h2 className="text-xl font-bold">Atualizar Módulo</h2>}
    >
      <Fade in={open}>
        <div className="text-xl grid gap-8">
          <form onSubmit={handleSubmit} className="grid gap-4">
            <ScrollArea className="h-[500px] w-full">
              <div className="grid gap-4">
                <div className="grid gap-3 md:grid-cols-2">
                  <CardModule
                    variant="edit"
                    editProps={{
                      defaultFile: selectedItem?.disableIcon,
                      label: 'Ícone desativado',
                      onIconFileChange: onDisabledIconChange,
                    }}
                  />

                  <CardModule
                    variant="edit"
                    editProps={{
                      defaultFile: selectedItem?.activeIcon,
                      label: 'Ícone ativo',
                      onIconFileChange: onActiveIconChange,
                    }}
                  />
                </div>

                <TextField
                  required
                  label="Nome"
                  name="name"
                  value={input.name}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                  error={!!input.errors.name}
                  helperText={input.errors.name}
                  disabled={loading}
                />

                <TextField
                  required
                  type="number"
                  inputProps={{ min: 1, max: 5 }}
                  label="Nível"
                  name="level"
                  value={input.level}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                  error={!!input.errors.level}
                  helperText={input.errors.level}
                  disabled={loading}
                />

                <TextField
                  required
                  minRows={4}
                  maxRows={8}
                  multiline
                  label="Descrição"
                  name="description"
                  value={input.description}
                  onChange={input.onChange}
                  onBlur={input.onBlur}
                  error={!!input.errors.description}
                  helperText={input.errors.description}
                  disabled={loading}
                />

                {loading && <Alert severity="warning">A enviar dados...</Alert>}
                {error && <Alert severity="error">{error}</Alert>}
              </div>
            </ScrollArea>

            <div className="w-max max-w-full ml-auto mt-4 gap-4 flex">
              <Button
                disabled={loading}
                variant="outline"
                type="reset"
                onClick={closeModal}
              >
                Cancelar
              </Button>
              <Button disabled={loading} type="submit">
                Atualizar
              </Button>
            </div>
          </form>
        </div>
      </Fade>
    </Modal>
  )
}
